import React from "react";
import { Box, VStack, Link, Icon, Text } from "@chakra-ui/react";
import { Badge } from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  FiLayout,
  FiBarChart2,
  FiUsers,
  FiUserCheck,
  FiTv,
  FiMusic,
  FiYoutube,
  FiDollarSign,
  FiHelpCircle,
  FiSettings,
  FiClipboard,
  FiVideo,
  FiShoppingBag,
  FiBell,
  FiGift,
  FiSmartphone,
  FiMessageCircle,
  FiGlobe,
  FiMonitor,
  FiTarget,
  FiShield,
  FiTag,
  FiCreditCard,
  FiEye,
  FiAward,
  FiSpeaker,
  FiTrendingUp,
  FiInbox,
  FiBookOpen,
  FiCalendar,
  FiKey,
  FiCpu, FiLink, FiFilm, FiPenTool, FiShare2,
} from "react-icons/fi";
import { useAdminFeatureNotices } from "./featureNotice/AdminFeatureNoticeContext";
import { unackedNotices } from "../../lib/adminFeatureNotice";
import { supabase } from "../../lib/supabaseClient";

// 「고쳤다는 건 확인」 판정 대기 건수(2026-09-27 · docs/ops/reupload-link-plan.md §5-3 R5) — 화면 표시용 배지일 뿐 알림이 아니다.
// 마운트 때 한 번 + 10분마다. 0 이거나 읽기 실패면 숨긴다.
const FIX_PENDING_POLL_MS = 10 * 60 * 1000;
function useFixCheckPendingCount() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { count: n, error } = await supabase.from("inspection_fix_check").select("inspection_id", { count: "exact", head: true }).is("verdict", null);
        if (alive) setCount(error ? 0 : n || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    load();
    const iv = setInterval(load, FIX_PENDING_POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  return count;
}

// 「문의」 배지 = 답변이 필요한 문의 + 크리에이터가 마지막으로 말한 열린 대화방(admin_inquiry_menu_badge). 화면 표시용 · 1분마다.
const INQUIRY_BADGE_POLL_MS = 60 * 1000;
function useInquiryBadgeCount() {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data, error } = await supabase.rpc("admin_inquiry_menu_badge");
        if (alive) setCount(error ? 0 : Number(data) || 0);
      } catch {
        if (alive) setCount(0);
      }
    };
    load();
    const iv = setInterval(load, INQUIRY_BADGE_POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  return count;
}

const menuGroups = [
  {
    // 새 기능 안내 원장(시안 C, 2026-09-14) — 안 읽은 안내 개수 배지. 데이터는 AdminFeatureNoticeProvider(AdminLayout).
    label: "새 소식",
    items: [{ icon: FiBell, label: "새 소식", href: "/admin/whats-new", badge: "whatsNew" }],
  },
  {
    // 문의 한 곳 — 탭 「문의 관리」(크리에이터 문의 티켓 · 2026-09-08 PR-D) · 「대화방 관리」(운영팀 대화방 · 2026-10-23).
    // 종전 「시스템 › 문의 관리」 자리는 비웠다. docs/ops/ops-chat-room-plan.md §3-2
    label: "문의",
    items: [{ icon: FiMessageCircle, label: "문의", href: "/admin/inquiries", badge: "inquiry" }],
  },
  {
    label: "콘텐츠 관리",
    items: [
      { icon: FiLayout, label: "대시보드", href: "/admin/dashboard" },
      { icon: FiUsers, label: "사용자", href: "/admin/users" },
      // 가입 유형 — 추천 코드로 묶은 회원 · 혜택 · 작품 유형 매핑(2026-09-30 PR-3 · docs/pages/admin/AdminSignupTypes.md)
      { icon: FiUserCheck, label: "가입 유형", href: "/admin/signup-types" },
      { icon: FiTv, label: "채널", href: "/admin/channels" },
      { icon: FiShare2, label: "협업 채널", href: "/admin/collab-channels" },  // CJ ENM 등 협업 회사 채널 — docs/pages/admin/AdminCollabChannels.md
      { icon: FiVideo, label: "채널-작품", href: "/admin/channel-licensed-video" },
      { icon: FiMonitor, label: "채널 모니터링", href: "/admin/channel-monitoring" },
      { icon: FiMusic, label: "음악", href: "/admin/music" },
      { icon: FiMusic, label: "음원 분류", href: "/admin/content-music" },
      { icon: FiYoutube, label: "작품", href: "/admin/videos" },
      { icon: FiClipboard, label: "작품 신청", href: "/admin/video-applications" },
      { icon: FiClipboard, label: "검수 관리", href: "/admin/inspections" },
      { icon: FiClipboard, label: "검수 준수 모니터링", href: "/admin/inspection-compliance", badge: "fixPending" },
      { icon: FiFilm, label: "완성 쇼츠", href: "/admin/ready-shorts" },
      { icon: FiEye, label: "엠바고 감시", href: "/admin/embargo" },
      { icon: FiClipboard, label: "본방 전 소재 계약", href: "/admin/prerelease" },
      ...(process.env.REACT_APP_PRERELEASE_SANDBOX === "1" ? [{ icon: FiClipboard, label: "본방 전 소재 체험판", href: "/admin/prerelease-sandbox" }] : []),
      { icon: FiAward, label: "크리에이터 등급 관리", href: "/admin/creator-tiers" },
      { icon: FiAward, label: "가감점 관리", href: "/admin/tier-adjustments" },  // 등급 가감점 한 곳 — 첫 탭 「의무 업로드」 (2026-10-21 · docs/pages/admin/AdminTierAdjustments.md)
      { icon: FiGift, label: "번들 관리", href: "/admin/promotions" },
    ],
  },
  {
    label: "수익 관리",
    items: [
      { icon: FiYoutube, label: "유튜브 수익", href: "/admin/video-revenue" },
      { icon: FiMusic, label: "음원 수익", href: "/admin/shorts-music-revenue" },
      { icon: FiMessageCircle, label: "카카오 조회수", href: "/admin/kakao-revenue" },
      { icon: FiGlobe, label: "네이버 조회수", href: "/admin/naver-revenue" },
    ],
  },
  {
    label: "정산 관리",
    items: [
      { icon: FiBarChart2, label: "정산 대시보드", href: "/admin/video-settlement-dashboard" },
      { icon: FiDollarSign, label: "음원 정산", href: "/admin/settlement" },
      { icon: FiDollarSign, label: "외부대중음악 정산", href: "/admin/public-music-settlement" },
      { icon: FiGift, label: "번들 정산", href: "/admin/promotion-settlement" },
      { icon: FiDollarSign, label: "영상 정산", href: "/admin/video-settlement" },
      { icon: FiDollarSign, label: "카카오/네이버 정산", href: "/admin/kakao-naver-video-settlement" },
      { icon: FiDollarSign, label: "파트너스 정산", href: "/admin/partner-settlement" },
      { icon: FiDollarSign, label: "정산 신청", href: "/admin/settlement-request" },
    ],
  },
  {
    label: "사용자 화면",
    items: [
      { icon: FiUsers, label: "사용자 화면", href: "/admin/user-settlement-view" },
    ],
  },
  {
    label: "시스템",
    items: [
      { icon: FiCreditCard, label: "멤버십", href: "/admin/billing" },  // 멤버십 구독료·결제 어드민 (2026-09-07 · 이름 2026-09-21) — docs/pages/admin/AdminBilling.md
      { icon: FiBell, label: "알림 관리", href: "/admin/notifications" },
      { icon: FiShoppingBag, label: "쇼핑 협업", href: "/admin/shopping-collaboration" },
      { icon: FiHelpCircle, label: "FAQ / 공지사항", href: "/admin/support" },
      { icon: FiSettings, label: "팝업 관리", href: "/admin/popup" },
      { icon: FiSmartphone, label: "앱 버전 관리", href: "/admin/app-version" },
      { icon: FiBarChart2, label: "유튜브 인사이트", href: "/admin/youtube-insights" },
    ],
  },
  {
    // 🔒 대표 지시 2026-09-10: 션(개발팀)이 만든 화면이 아니라 대표가 클로드로 만든 화면은 모두 「부가기능」에 둔다.
    //    위 「콘텐츠 관리」「수익 관리」「정산 관리」「시스템」은 개발팀 화면. 새 화면을 만들면 여기에 추가한다.
    label: "부가기능",
    items: [
      // 레이블리 캘린더 — 예약 페이지·시간표·예약·참석자·구글 캘린더 연동을 한 페이지에 (2026-09-10 대표 결정 시안 C, admin.rhoonart.com 에서 옮겨옴)
      { icon: FiCalendar, label: "레이블리 캘린더", href: "/admin/calendar" },
      { icon: FiTrendingUp, label: "성과 시뮬레이터", href: "/admin/revenue-simulator" },
      { icon: FiInbox, label: "브랜드 채널 문의", href: "/admin/brand-inquiries" },
      // 소통·등급·추천·레슨 — main 에 있던 항목(lay 브랜치 머지 때 「부가기능」 재배치, 2026-09-10)
      { icon: FiSpeaker, label: "공지 관리", href: "/admin/announcements" },
      { icon: FiGlobe, label: "플랫폼 소식", href: "/admin/platform-news" },
      { icon: FiAward, label: "등급 가점", href: "/admin/tier-bonus" },
      { icon: FiTarget, label: "추천 모니터링", href: "/admin/recommendation" },
      { icon: FiBarChart2, label: "레슨 이수·오답 통계", href: "/admin/lesson-stats" },
      { icon: FiEye, label: "신청 레슨·동의·알림 체험", href: "/admin/work-consent-preview" },
      // #164 가 「크리에이터 판정 관리」+「정책 동의 현황」을 한 메뉴로 통합했다 —
      // 되살리면 구 경로(리다이렉트)를 가리키는 중복 메뉴가 된다.
      { icon: FiShield, label: "숏폼 정책 관리", href: "/admin/policy-consents" },
      // 레이 AI 도우미 — 시키기·승인·답변 사전·확인할 답변·받는 사람 묶음 (2026-09-09 로드맵 2단계, 2026-09-10 메뉴명 정리)
      { icon: FiCpu, label: "레이 AI 도우미", href: "/admin/lay" },
      // 레이 원탁 상황판 — 제안자·다섯 편·반장 회의를 보고 대표가 결정 (2026-09-11 1단계, AdminLay 「원탁」 탭의 바로가기)
      { icon: FiUsers, label: "레이 회의실", href: "/admin/lay/council" },
      { icon: FiCpu, label: "화면 개선 제안", href: "/admin/lay/ux" },
      // docs.laeebly.io 원고 편집 — admin.rhoonart.com 에서 옮겨옴 (2026-09-06 대표 지시)
      { icon: FiBookOpen, label: "레이블리 가이드", href: "/admin/laeebly-guide" },
      // 공지·FAQ 의 영어·일본어·중국어 번역 관리 (2026-09-07)
      { icon: FiGlobe, label: "콘텐츠 번역", href: "/admin/content-translation" },
      // 화면 전체가 같이 쓰는 표시·심볼 원본과 결정 기록 — 채널 종류 심볼부터 (2026-10-20 대표 지시)
      { icon: FiPenTool, label: "디자인센터", href: "/admin/design-center" },
      // 대외로 보내는 주소를 모아 두는 곳 — 줄마다 언어·상태 (2026-09-10 대표 지시)
      { icon: FiLink, label: "링크 모음", href: "/admin/links" },
      // 「[공지방] 레이블리 - 유튜브」 오픈채팅 — 매일 참여자 닉네임 대조 · 방 설정 · 코드 찾기 (2026-10-29 · docs/pages/admin/AdminOpenChat.md)
      { icon: FiMessageCircle, label: "오픈채팅 입장 확인", href: "/admin/openchat" },
      // 권리사 미팅에서 「원본 → 쇼츠 → 왜 그 장면인지」를 보여 주는 사례 — 미팅 화면은 /showroom (2026-10-27 대표 결정)
      { icon: FiFilm, label: "제작 사례 쇼룸", href: "/admin/showroom" },
      // 파트너 원장·유입 집계 (2026-09-09, 파트너 허브 1차)
      { icon: FiUsers, label: "파트너 관리", href: "/admin/partners" },
      // /shorts 입장 코드 — 신규 고객 기간 한정 열람 + 행동 기록 (2026-09-10 대표 지시, 「부가기능」 아래)
      { icon: FiKey, label: "쇼츠 입장 코드", href: "/admin/watch-codes" },
      // 날짜가 되면 사람이 해야 하는 일(약관 시행 스위치·과금 시작 …) — 2026-09-10 대표 지시
      { icon: FiCalendar, label: "예정 작업", href: "/admin/scheduled-tasks" },
      // 채널 등록 구글 OAuth — grant 현황·실행 원장·스위치 6개 (2026-09-23, docs/pages/admin/AdminYoutubeOAuth.md)
      { icon: FiYoutube, label: "유튜브 연결", href: "/admin/youtube-oauth" },
      // 작품별 검수 정책·지오블락 나라 목록 — 대표가 미지정 작품을 채우는 표 (2026-09-12 검수·지오블락 PR-B)
      { icon: FiClipboard, label: "작품 검수 정책", href: "/admin/inspection-policy" },
      // 제휴 코드(결제 프로모션, 2026-09-07) — docs/pages/admin/AdminPromoCodes.md. /admin/promotions(프로모션 정산)와 다른 화면
      { icon: FiTag, label: "제휴 코드", href: "/admin/promo-codes" },
    ],
  },
];

const Sidebar = ({ isMobile = false, onClose }) => {
  const location = useLocation();
  const { notices, me } = useAdminFeatureNotices();
  const whatsNewCount = unackedNotices(notices, me).length;
  const fixPendingCount = useFixCheckPendingCount();
  const inquiryCount = useInquiryBadgeCount();

  return (
    <Box
      minW={isMobile ? "auto" : "250px"}
      w={isMobile ? "100%" : "250px"}
      bg={isMobile ? "transparent" : "neutral.900"}
      h={isMobile ? "100%" : "calc(100vh - 60px)"}
      pt={isMobile ? 10 : 4}
      pb={4}
      px={3}
      position={isMobile ? "relative" : "sticky"}
      top={isMobile ? undefined : "60px"}
      overflowY="auto"
      className="admin-scroll"
      display={isMobile ? "block" : { base: "none", lg: "block" }}
      sx={isMobile ? { WebkitOverflowScrolling: "touch" } : undefined}
    >
      {menuGroups.map((group) => (
        <Box key={group.label} mb={5}>
          <Text
            fontSize="xs"
            fontWeight="600"
            color="neutral.500"
            letterSpacing="0.05em"
            px={3}
            mb={2}
          >
            {group.label}
          </Text>
          <VStack align="stretch" spacing={0.5}>
            {group.items.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`); // 상세 라우트(/admin/recommendation/:id)도 활성
              return (
                <Link
                  key={item.href}
                  as={RouterLink}
                  to={item.href}
                  onClick={onClose}
                  display="flex"
                  alignItems="center"
                  px={3}
                  py={isMobile ? 3 : 2}
                  borderRadius="lg"
                  bg={isActive ? "rgba(255,255,255,0.08)" : "transparent"}
                  color={isActive ? "white" : "neutral.400"}
                  fontWeight={isActive ? "600" : "400"}
                  fontSize="sm"
                  borderLeft="3px solid"
                  borderLeftColor={isActive ? "brand.400" : "transparent"}
                  transition="all 0.15s ease"
                  _hover={{
                    bg: isActive ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                    color: isActive ? "white" : "neutral.200",
                    textDecoration: "none",
                  }}
                >
                  <Icon
                    as={item.icon}
                    mr={3}
                    boxSize={4}
                    color={isActive ? "brand.400" : "neutral.500"}
                    transition="color 0.15s ease"
                  />
                  <Text>{item.label}</Text>
                  {item.badge === "whatsNew" && whatsNewCount > 0 && (
                    <Badge ml="auto" bg="brand.500" color="white" borderRadius="full" fontSize="10px" px={1.5} minW="18px" textAlign="center">
                      {whatsNewCount}
                    </Badge>
                  )}
                  {item.badge === "inquiry" && inquiryCount > 0 && (
                    <Badge ml="auto" bg="status.error" color="white" borderRadius="full" fontSize="10px" px={1.5} minW="18px" textAlign="center" title="답변이 필요한 문의 · 답장 온 대화방">
                      {inquiryCount}
                    </Badge>
                  )}
                  {item.badge === "fixPending" && fixPendingCount > 0 && (
                    <Badge ml="auto" bg="status.error" color="white" borderRadius="full" fontSize="10px" px={1.5} minW="18px" textAlign="center" title="고쳤다는 건 확인 · 판정 대기" data-fix-pending>
                      {fixPendingCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </VStack>
        </Box>
      ))}
    </Box>
  );
};

export default React.memo(Sidebar);
